"use client"

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams, Link } from 'react-router-dom';
import { api } from '../lib/api';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { BookOpen, Loader2, CheckCircle2, AlertCircle } from 'lucide-react';

type Status = 'loading' | 'success' | 'error';

export function VerifyEmailPage() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';
  const [status, setStatus] = useState<Status>('loading');
  const [errorMessage, setErrorMessage] = useState<string>('');

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setErrorMessage(t('verifyEmail.invalidOrMissingToken', 'Token inválido ou ausente.'));
      return;
    }

    api
      .verifyEmail(token)
      .then(() => setStatus('success'))
      .catch((err) => {
        const msg =
          err && typeof err === 'object' && 'message' in err
            ? String((err as { message: string }).message)
            : t('verifyEmail.invalidOrExpiredToken', 'Token inválido ou expirado.');
        setErrorMessage(msg);
        setStatus('error');
      });
  }, [token]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-background via-background to-muted/30 p-4">
      <div className="w-full max-w-md">
        <div className="mb-8 flex flex-col items-center text-center">
          <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-primary text-primary-foreground">
            <BookOpen className="h-7 w-7" />
          </div>
          <h1 className="text-2xl font-bold tracking-tight">reformu.lat</h1>
        </div>

        <Card className="border-border/50 shadow-lg">
          <CardHeader className="space-y-1 pb-4">
            <CardTitle className="text-xl">{t('verifyEmail.emailVerificationTitle', 'Verificação de e-mail')}</CardTitle>
            <CardDescription>
              {t('verifyEmail.confirmingYourEmail', 'Confirmando seu endereço de e-mail...')}
            </CardDescription>
          </CardHeader>

          <CardContent>
            {status === 'loading' && (
              <div className="flex flex-col items-center gap-3 py-6">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
                <p className="text-sm text-muted-foreground">{t('verifyEmail.verifying', 'Verificando...')}</p>
              </div>
            )}

            {status === 'success' && (
              <div className="flex flex-col items-center gap-3 py-6 text-center">
                <CheckCircle2 className="h-10 w-10 text-primary" />
                <p className="font-medium">{t('verifyEmail.emailVerifiedSuccess', 'E-mail verificado com sucesso!')}</p>
                <p className="text-sm text-muted-foreground">
                  {t('verifyEmail.accountConfirmedMessage', 'Sua conta está confirmada. Você já pode usar todas as funcionalidades da plataforma.')}
                </p>
                <Link
                  to="/app"
                  className="mt-2 text-sm font-medium text-primary hover:underline"
                >
                  {t('verifyEmail.goToDashboard', 'Ir para o painel')}
                </Link>
              </div>
            )}

            {status === 'error' && (
              <div className="flex flex-col gap-4 py-2">
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{errorMessage}</AlertDescription>
                </Alert>
                <p className="text-sm text-muted-foreground text-center">
                  {t('verifyEmail.requestNewVerificationLink', 'Solicite um novo link de verificação na sua conta.')}
                </p>
                <div className="flex justify-center">
                  <Link
                    to="/app/account"
                    className="text-sm font-medium text-primary hover:underline"
                  >
                    {t('verifyEmail.goToMyAccount', 'Ir para minha conta')}
                  </Link>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
